/**
 * Équipement, craft et jets aléatoires (GDD §6). Module pur.
 *
 * Le craft n'a aucun niveau requis : les matériaux sont le seul verrou. Toute
 * la profondeur vient du **jet** : chaque statistique est tirée dans sa
 * fourchette officielle, et re-crafter pour un meilleur jet est le puits à
 * matériaux qui fait qu'une zone n'est jamais « finie ».
 */
import type { Contenu, Item } from "./contenu.ts"
import { item as parId } from "./contenu.ts"

// ---------------------------------------------------------------- emplacements

export const SLOTS = [
  "coiffe", "cape", "amulette", "anneau1", "anneau2",
  "ceinture", "bottes", "arme", "bouclier", "familier",
] as const
export type Slot = (typeof SLOTS)[number]

export const LIBELLE_SLOT: Record<Slot, string> = {
  coiffe: "Coiffe", cape: "Cape", amulette: "Amulette",
  anneau1: "Anneau", anneau2: "Anneau", ceinture: "Ceinture",
  bottes: "Bottes", arme: "Arme", bouclier: "Bouclier", familier: "Familier",
}

/** Type d'objet Dofus → emplacement. Les armes partagent le même. */
const PAR_TYPE: Record<string, Slot> = {
  Chapeau: "coiffe", Cape: "cape", Amulette: "amulette", Anneau: "anneau1",
  Ceinture: "ceinture", Bottes: "bottes", Bouclier: "bouclier", Familier: "familier",
  "Épée": "arme", Marteau: "arme", "Bâton": "arme", Pelle: "arme", Hache: "arme",
  Dague: "arme", Arc: "arme", Baguette: "arme", Faux: "arme", Pioche: "arme",
}

export const slotDe = (it: Item): Slot | null => PAR_TYPE[it.type] ?? null

/**
 * On ne porte que ce qu'on a le niveau de porter, comme dans le jeu d'origine.
 *
 * Sans cette règle, une paire de bottes de niveau 200 tombée par chance au
 * niveau 3 rendait tout le reste de la progression sans objet : les
 * caractéristiques d'un objet de haut niveau écrasent tout ce qu'on peut
 * fabriquer pendant les cent niveaux suivants.
 */
export const portable = (it: Item, niveauJoueur: number): boolean =>
  it.niveau <= niveauJoueur

/**
 * Un exemplaire d'équipement.
 *
 * C'est une **instance**, pas une quantité : deux Coiffes du Bouftou craftées
 * séparément ont des jets différents et doivent coexister. L'inventaire de
 * ressources reste un compteur ; l'équipement, lui, est une liste d'objets.
 */
export interface Porte {
  /** identifiant d'exemplaire, unique dans le compte */
  uid: string
  item: number
  /** effectId → valeur tirée */
  jet: Record<number, number>
}

// ---------------------------------------------------------------- effets

const VITALITE = 125, FORCE = 118, INTELLIGENCE = 126, CHANCE = 123,
      AGILITE = 119, SAGESSE = 124, PROSPECTION = 176, SOIN = 178
const PUISSANCE = [138, 701]
const PA = 111, PM = 128
/**
 * Les statistiques longtemps affichées sans rien faire.
 *
 * Elles couvrent la moitié du catalogue — 846 objets donnent du % Critique,
 * 431 de la Portée, 370 de l'Initiative — et n'avaient aucun effet : on lisait
 * une fiche d'objet dont la moitié des lignes étaient décoratives. Elles
 * alimentent maintenant quatre systèmes lisibles (GDD §4).
 */
const CRITIQUE = 115, DOM_CRITIQUE = 418
const PORTEE = 117, INITIATIVE = 174, INVOCATION = 182, POD = 158
const FUITE = 752, TACLE = 753
/**
 * Les deux familles de dégâts des données d'Ankama, longtemps confondues.
 *
 * `96-100` (« dommages Terre », minuscule) sont les coups de l'arme elle-même ;
 * `112` et `422-430` (« Dommage Terre », majuscule) sont la caractéristique
 * Dommages, celle qui s'ajoute à chaque frappe. Le code n'écoutait que la
 * première série et l'identifiant `121`, qu'aucun objet de nos données n'emploie :
 * les 375 objets qui donnent « Dommage » et les ~390 qui en donnent par élément
 * affichaient une statistique qui ne servait à rien.
 *
 * **Le Neutre vaut pour tous les éléments**, il n'est plus versé en Terre.
 *
 * Notre modèle n'a que quatre éléments, et le Neutre était rangé en Terre parce
 * que chez Ankama il monte avec la Force. Conséquence : les 717 objets qui
 * donnent des dégâts neutres n'apportaient rien à qui ne joue pas la Terre — et
 * pire, ils gonflaient un élément inutilisé, ce que `dpsContre` peut élire comme
 * « meilleur élément » et faire passer une arme pour bonne. Balbouz l'a mesuré
 * sur son Enu Eau le 27/08/2026 : en troquant 40 dégâts neutres contre 21
 * neutres + 21 Eau, il a **perdu** des dégâts là où il en attendait.
 *
 * Or le Neutre est ce que l'arme inflige quoi qu'on joue : il rejoint donc les
 * dégâts « tous éléments », ce qui est à la fois plus juste et sans piège.
 */
const DOMMAGES_TOUS = [112, 121, 100, 430]
const DOM_ELEM: Record<number, "terre" | "feu" | "eau" | "air"> = {
  97: "terre", 99: "feu", 96: "eau", 98: "air",
  422: "terre", 424: "feu", 426: "eau", 428: "air",
  1063: "terre", 1066: "feu", 1065: "eau", 1064: "air",
}
const RES_PCT: Record<number, "terre" | "feu" | "eau" | "air"> =
  { 210: "terre", 213: "feu", 211: "eau", 212: "air" }

export interface Bonus {
  vitalite: number; force: number; intelligence: number; chance: number
  agilite: number; sagesse: number; prospection: number; soin: number; puissance: number
  /** points d'action : ils accélèrent la cadence d'attaque */
  pa: number
  /** points de mouvement : ils accélèrent la récolte */
  pm: number
  /** chance qu'un coup soit critique, en pourcentage */
  critique: number
  /** dégâts supplémentaires portés par un coup critique */
  domCritique: number
  /** portée et initiative : elles décident de l'entame du combat */
  portee: number
  initiative: number
  /** invocations : elles combattent à vos côtés */
  invocation: number
  /** pods : ce que l'équipe peut entreposer */
  pods: number
  /** fuite et tacle : elles alimentent l'esquive */
  fuite: number
  tacle: number
  dommages: Record<"terre" | "feu" | "eau" | "air", number>
  resistances: Record<"terre" | "feu" | "eau" | "air", number>
}

const bonusVide = (): Bonus => ({
  vitalite: 0, force: 0, intelligence: 0, chance: 0, agilite: 0, sagesse: 0,
  prospection: 0, soin: 0, puissance: 0, pa: 0, pm: 0,
  critique: 0, domCritique: 0, portee: 0, initiative: 0, invocation: 0,
  pods: 0, fuite: 0, tacle: 0,
  dommages: { terre: 0, feu: 0, eau: 0, air: 0 },
  resistances: { terre: 0, feu: 0, eau: 0, air: 0 },
})

/** Applique un effet chiffré au cumul. */
function appliquer(b: Bonus, e: number, v: number) {
  if (e === VITALITE) b.vitalite += v
  else if (e === FORCE) b.force += v
  else if (e === INTELLIGENCE) b.intelligence += v
  else if (e === CHANCE) b.chance += v
  else if (e === AGILITE) b.agilite += v
  else if (e === SAGESSE) b.sagesse += v
  else if (e === PROSPECTION) b.prospection += v
  else if (e === SOIN) b.soin += v
  else if (e === PA) b.pa += v
  else if (e === PM) b.pm += v
  else if (e === CRITIQUE) b.critique += v
  else if (e === DOM_CRITIQUE) b.domCritique += v
  else if (e === PORTEE) b.portee += v
  else if (e === INITIATIVE) b.initiative += v
  else if (e === INVOCATION) b.invocation += v
  else if (e === POD) b.pods += v
  else if (e === FUITE) b.fuite += v
  else if (e === TACLE) b.tacle += v
  else if (PUISSANCE.includes(e)) b.puissance += v
  else if (DOMMAGES_TOUS.includes(e)) for (const k of ["terre", "feu", "eau", "air"] as const) b.dommages[k] += v
  else if (DOM_ELEM[e]) b.dommages[DOM_ELEM[e]!] += v
  else if (RES_PCT[e]) b.resistances[RES_PCT[e]!] += v
}

/** Nombre de pièces portées pour chaque panoplie. */
export function panoplies(equipement: Partial<Record<Slot, Porte>>, c: Contenu): Map<number, number> {
  // Les **objets distincts**, pas les emplacements remplis : les deux anneaux
  // peuvent porter le même objet, et la panoplie du Piou Vert affichait alors
  // « 7 / 6 », en accordant pour de bon le palier 7 d'un set qui n'a que six
  // pièces. Dofus compte des pièces différentes.
  const vus = new Map<number, Set<number>>()
  for (const p of Object.values(equipement)) {
    const it = p ? parId(c, p.item) : null
    if (it?.panoplie == null) continue
    const s = vus.get(it.panoplie) ?? new Set<number>()
    s.add(it.id)
    vus.set(it.panoplie, s)
  }
  return new Map([...vus].map(([id, s]) => [id, s.size]))
}

/**
 * Palier de panoplie atteint avec `n` pièces : le plus haut seuil qui ne dépasse
 * pas `n`. Les tables d'Ankama ne sont pas toujours contiguës, et une
 * correspondance exacte perdait le bonus dès qu'un seuil manquait.
 */
export function palierPanoplie(pano: { paliers?: Record<string, { effet: number; valeur: number }[]> } | undefined, n: number) {
  if (!pano?.paliers) return null
  const seuils = Object.keys(pano.paliers).map(Number).filter((k) => k <= n)
  if (seuils.length === 0) return null
  const s = Math.max(...seuils)
  return { seuil: s, effets: pano.paliers[String(s)]! }
}

/**
 * Somme des effets de l'équipement porté, **bonus de panoplie compris**.
 *
 * Les panoplies transforment l'équipement d'une liste de chiffres en collection
 * à compléter : les paliers sont ceux d'Ankama, indexés par nombre de pièces
 * (GDD §10).
 */
export function bonusEquipement(equipement: Partial<Record<Slot, Porte>>, c?: Contenu): Bonus {
  const b = bonusVide()
  for (const p of Object.values(equipement)) {
    if (!p) continue
    for (const [id, v] of Object.entries(p.jet)) {
      const e = Number(id)
      appliquer(b, e, v)
    }
  }

  if (c) {
    for (const [setId, n] of panoplies(equipement, c)) {
      // On applique le palier atteint, pas le cumul des paliers : chaque entrée
      // est déjà la valeur totale du set à ce nombre de pièces.
      const atteint = palierPanoplie(c.panoplies[String(setId)], n)
      if (atteint) for (const e of atteint.effets) appliquer(b, e.effet, e.valeur)
    }
  }
  return b
}

// ---------------------------------------------------------------- le jet

/**
 * Tire un objet dans ses fourchettes officielles.
 *
 * `doubleJet` correspond à l'Écaflip (GDD §3) : on tire deux fois et on garde
 * le meilleur. C'est la classe branchée sur le système de craft.
 */
export function jeter(it: Item, rng: () => number, doubleJet = false, uid = ""): Porte {
  const tirage = (): Record<number, number> => {
    const j: Record<number, number> = {}
    for (const e of it.effets) {
      j[e.effet] = e.min + Math.floor(rng() * (e.max - e.min + 1))
    }
    return j
  }
  const id = uid || `x${Math.floor(rng() * 1e12).toString(36)}`
  if (!doubleJet) return { uid: id, item: it.id, jet: tirage() }
  const a: Porte = { uid: id, item: it.id, jet: tirage() }
  const b: Porte = { uid: id, item: it.id, jet: tirage() }
  return perfection(it, b) > perfection(it, a) ? b : a
}

/** Position du jet dans ses fourchettes, en pourcentage. 100 = parfait. */
export function perfection(it: Item, p: Porte): number {
  const utiles = it.effets.filter((e) => e.max > e.min)
  if (utiles.length === 0) return 100
  const somme = utiles.reduce((a, e) => {
    const v = p.jet[e.effet] ?? e.min
    return a + (v - e.min) / (e.max - e.min)
  }, 0)
  return Math.round((somme / utiles.length) * 100)
}

// ---------------------------------------------------------------- se soigner

/**
 * Délai entre deux repas.
 *
 * Il valait trente secondes du temps où un vivre rendait une **part de la
 * barre** : sans lui on enchaînait les pains et l'on devenait intouchable.
 * Depuis que le soin est la valeur brute de l'objet, ce n'est plus le délai qui
 * empêche l'abus mais **le vivre lui-même et la réserve** — survivre à du
 * contenu très au-dessus demanderait des milliers de pains à l'heure.
 *
 * Trente secondes rendaient alors les vivres dérisoires : douze points de vie
 * par demi-minute contre quatre perdus par seconde. Cinq secondes gardent le
 * rythme — on ne vide pas dix pains d'un coup au milieu d'un combat — et
 * laissent au **vivre adapté à son niveau** de quoi tenir, là où un vivre
 * dépassé ne fait plus que retarder la chute. C'est ce qui donne une raison de
 * monter son métier de cuisinier.
 */
export const DELAI_REPAS_MS = 5_000

/** Effet « Rend points de vie » dans les données du jeu. */
const REND_PV = 110

/**
 * Effet « Rend point d'énergie », et sa conversion en points de vie.
 *
 * **Trente-neuf objets typés nourriture ne nourrissaient pas.** Les Boulettes de
 * Viande, les Bâtons de Crabe, le Pain des Villes : leur seul effet est de
 * rendre de l'énergie, un système que ce jeu n'a pas. Ils tombaient donc dans le
 * sac, ressemblaient à des vivres, et ne servaient absolument à rien — Vyshu a
 * signalé les deux premiers à un quart d'heure d'intervalle, sans faire le lien.
 *
 * Plutôt que de les cacher, on les rend comestibles. Le taux n'est pas choisi :
 * à niveau égal, la valeur d'énergie des données d'Ankama vaut environ le triple
 * de la valeur de vie (niveau 100 : 330 contre 110 ; niveau 150 : 510 contre
 * 170). Un tiers place donc ces vivres exactement au niveau de ceux qui
 * existaient déjà, sans créer de raccourci.
 */
const REND_ENERGIE = 139
const ENERGIE_EN_PV = 1 / 3

/**
 * Les types d'objets qu'on consomme. Le filtre porte sur le **type** et pas sur
 * le seul effet : des armes portent aussi « Rend points de vie », en vol de vie,
 * et on ne les mange pas.
 */
const CONSOMMABLES = new Set([
  "Pain", "Potion", "Viande comestible", "Poisson comestible", "Friandise",
])

export const estConsommable = (it: Item): boolean =>
  CONSOMMABLES.has(it.type)
  && it.effets.some((e) => e.effet === REND_PV || e.effet === REND_ENERGIE)

/**
 * Part de la barre de vie que rend un objet, entre 0 et 1.
 *
 * La valeur brute des données ne veut rien dire hors de son contexte : un Pain
 * d'Incarnam rend 12 points, ce qui est un cinquième de la vie d'un débutant et
 * une goutte pour un personnage de niveau 200. On lit donc ce que l'objet
 * rendrait au personnage **pour lequel il a été conçu**, et on applique cette
 * proportion à celui qui le mange. Un pain reste un pain à tout niveau, une
 * potion de haut niveau reste meilleure.
 */
/**
 * Ce qu'un vivre rend, avant le bonus de Soin.
 *
 * **La valeur brute des données d'Ankama, telle quelle.** Un premier modèle la
 * lisait comme une *part de la barre* que l'objet visait, pour qu'un pain de
 * niveau 1 reste utile à haut niveau : une Cervelle de larve annonçait « +11 »
 * et rendait trois cents points de vie. C'était incompréhensible, et surtout
 * c'était le contraire de ce qu'on veut — qu'un vivre de bas niveau devienne
 * dérisoire est la règle, pas le problème à résoudre. Elle donne une raison de
 * monter son métier de cuisinier.
 *
 * Ce qui fait qu'un vivre reste intéressant, c'est la statistique **Soin** de
 * l'équipement, qui multiplie ce chiffre — jusqu'à le tripler.
 */
export const soinBrut = (it: Item): number => {
  const e = it.effets.find((x) => x.effet === REND_PV)
  if (e) return Math.round((e.min + e.max) / 2)
  // Faute de mieux, l'énergie : voir `ENERGIE_EN_PV`. Un plancher de 1 pour
  // qu'un vivre reste un vivre — sans quoi les plus petits soigneraient zéro et
  // se retrouveraient dans le sac sans plus d'usage qu'avant.
  const g = it.effets.find((x) => x.effet === REND_ENERGIE)
  return g ? Math.max(1, Math.round(((g.min + g.max) / 2) * ENERGIE_EN_PV)) : 0
}


// ---------------------------------------------------------------- broyage

/** Pépite et Tourmaline : ce que rend le broyage, et rien d'autre. */
export const PEPITE = 14635
export const TOURMALINE = 15271

/**
 * Ce que rend le broyage d'une pièce.
 *
 * Quatorze matériaux de haut niveau n'ont aucune source dans les données : ils
 * viennent, dans Dofus, de systèmes que ce jeu n'a pas. Douze ont été rattachés
 * à une famille ou à un boss ; les deux derniers, Pépite et Tourmaline, sont
 * produits par le **broyeur**, comme dans le jeu d'origine. Ils débloquent à eux
 * seuls près de 400 recettes d'équipement de haut niveau.
 *
 * Le rendement ne dépend que du **niveau** de la pièce, jamais de son jet : on
 * broie précisément ce qu'on ne veut pas porter, punir un mauvais jet une
 * seconde fois n'aurait aucun sens.
 */
export function broyage(it: Item): Record<number, number> {
  const r: Record<number, number> = { [PEPITE]: 1 + Math.floor(it.niveau / 20) }
  // La Tourmaline ne sort que d'une pièce déjà sérieuse.
  if (it.niveau >= 60) r[TOURMALINE] = 1 + Math.floor((it.niveau - 60) / 50)
  return r
}

// ---------------------------------------------------------------- recettes

export interface Recette {
  resultat: number
  niveau: number
  ingredients: { item: number; qte: number }[]
}

/** Ce qui manque pour lancer une recette. Vide = craftable. */
export function manque(r: Recette, inventaire: Record<number, number>) {
  return r.ingredients
    .map((g) => ({ item: g.item, manquant: g.qte - (inventaire[g.item] ?? 0) }))
    .filter((m) => m.manquant > 0)
}

export const craftable = (r: Recette, inv: Record<number, number>) => manque(r, inv).length === 0

/** Consomme les ingrédients. L'appelant a vérifié `craftable` au préalable. */
export function consommer(inventaire: Record<number, number>, r: Recette): Record<number, number> {
  const inv = { ...inventaire }
  for (const g of r.ingredients) {
    const reste = (inv[g.item] ?? 0) - g.qte
    if (reste > 0) inv[g.item] = reste
    else delete inv[g.item]
  }
  return inv
}

/**
 * Le dump mêle aux statistiques des effets techniques dont la « valeur » est un
 * identifiant : `effet#1175`, `Attitude 186`, `Titre 98`. Ils s'afficheraient
 * comme des bonus délirants sur les objets les plus prestigieux du jeu.
 * Ils ne comptent déjà ni dans `perfection` (fixes) ni dans les caractéristiques.
 */
const COSMETIQUES = new Set(["Attitude", "Titre", "Transforme en", "Gagne x", "/"])
export const effetLisible = (stat: string) =>
  !stat.startsWith("effet#") && !COSMETIQUES.has(stat) &&
  !/^(Affronte|Quelqu'un vous suit|Qu'y a-t-il)/.test(stat)

/** Résumé lisible d'un jet, pour l'affichage. */
export function detailJet(c: Contenu, p: Porte) {
  const it = parId(c, p.item)
  if (!it) return []
  return it.effets.filter((e) => effetLisible(e.stat)).map((e) => ({
    stat: e.stat,
    valeur: p.jet[e.effet] ?? e.min,
    min: e.min,
    max: e.max,
    fixe: e.max === e.min,
  }))
}
